import React, { useState } from "react";
import { Row, Col, Typography, Tag, Card, Flex } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import EditSupplierModal from "./EditSupplierModal";
import CustomModal from "../../../components/CustomModal/CustomModal";
import ReasonForm from "../../../components/CustomForms/ReasonForm";
import { useDispatch, useSelector } from "react-redux";
import { updateSupplierStatus, getSupplierById } from "../../../redux/slices/supplierSlice";
import { message } from "antd";
import { formatDate } from "../../../helpers/helpers";

const { Title, Text } = Typography;

const InfoItem = ({ label, value, tag, strong = false }) => (
  <div style={{ marginBottom: 16 }}>
    <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 2 }}>
      {label}
    </Text>
    {tag ? (
      tag
    ) : (
      <Text strong={strong} style={{ fontSize: 15, color: "#101928" }}>
        {value || "—"}
      </Text>
    )}
  </div>
);

const SupplierInfo = ({ supplier, onRefresh }) => {
  const dispatch = useDispatch();
  const { statusLoading } = useSelector((state) => state.supplier);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [reasonModal, setReasonModal] = useState({ open: false, type: null });

  if (!supplier) return null;

  const isActive = supplier.status === "ACTIVE";

  const handleStatusSubmit = async (values) => {
    const reason = typeof values === "string" ? values : (values?.reason || values?.remarks || "");
    const nextStatus = reasonModal.type === "deactivate" ? "INACTIVE" : "ACTIVE";
    const res = await dispatch(
      updateSupplierStatus({
        supplierId: supplier.supplierId,
        status: nextStatus,
        remarks: reason,
        reason: reason,
      })
    );

    if (
      res?.meta?.requestStatus === "fulfilled" &&
      (res?.payload?.statusCode === 200 ||
        res?.payload?.data?.msg ||
        res?.payload?.msg)
    ) {
      message.success(
        `Supplier status updated to ${nextStatus}.`
      );
      setReasonModal({ open: false, type: null });
      dispatch(getSupplierById(supplier.supplierId));
      onRefresh?.();
    } else {
      const errMsg =
        res?.payload?.response?.data?.message ||
        res?.payload?.message ||
        res?.payload ||
        "Failed to update status.";
      message.error(typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg));
    }
  };

  return (
    <div style={{ padding: "8px 0" }}>
      {/* Top Header Actions */}
      <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
        <div>
          <Title level={4} style={{ margin: 0, color: "#101928" }}>
            {supplier.supplierName}
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Supplier ID: <span style={{ fontWeight: 600 }}>{supplier.supplierId}</span>
          </Text>
        </div>
      </Flex>

      <Row gutter={[24, 24]}>
        {/* Section 1: Business & KYC Details */}
        <Col xs={24} md={12}>
          <Card
            title="Business & KYC Information"
            bordered={false}
            style={{ background: "#f8f9fc", borderRadius: 8, height: "100%" }}
          >
            <Row gutter={[16, 12]}>
              <Col span={12}>
                <InfoItem label="Supplier Name" value={supplier.supplierName} strong />
              </Col>
              <Col span={12}>
                <InfoItem
                  label="Status"
                  tag={
                    <Tag color={isActive ? "success" : "error"} style={{ borderRadius: 12 }}>
                      {supplier.status}
                    </Tag>
                  }
                />
              </Col>

              <Col span={12}>
                <InfoItem label="PAN Number" value={supplier.pan} />
              </Col>
              <Col span={12}>
                <InfoItem label="GSTIN Number" value={supplier.gstin} />
              </Col>

              <Col span={12}>
                <InfoItem
                  label="Onboarded On"
                  value={supplier.createdAt ? formatDate(supplier.createdAt) : "—"}
                />
              </Col>
              <Col span={12}>
                <InfoItem
                  label="Last Modified"
                  value={supplier.modifiedAt ? formatDate(supplier.modifiedAt) : "—"}
                />
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Section 2: Contact Person (POC) Details */}
        <Col xs={24} md={12}>
          <Card
            title="Contact Person (POC) Information"
            bordered={false}
            style={{ background: "#f8f9fc", borderRadius: 8, height: "100%" }}
          >
            <Row gutter={[16, 12]}>
              <Col span={24}>
                <InfoItem label="Contact Name" value={supplier.pocName} strong />
              </Col>
              <Col span={12}>
                <InfoItem label="Mobile Number" value={supplier.pocMobile ? `+91 ${supplier.pocMobile}` : "—"} />
              </Col>
              <Col span={12}>
                <InfoItem label="Email Address" value={supplier.pocEmail} />
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Section 3: Bank & Settlement Details */}
        <Col xs={24}>
          <Card
            title="Bank & Settlement Details"
            bordered={false}
            style={{ background: "#f8f9fc", borderRadius: 8 }}
          >
            <Row gutter={[16, 12]}>
              <Col xs={24} sm={12} md={8}>
                <InfoItem label="Account Holder Name" value={supplier.accHolderName} strong />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <InfoItem label="Bank Account Number" value={supplier.accNum} />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <InfoItem label="IFSC Code" value={supplier.ifsc} />
              </Col>

              <Col xs={24} sm={12} md={8}>
                <InfoItem label="Bank Name" value={supplier.bankName} />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <InfoItem label="Branch Name" value={supplier.branchName} />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <InfoItem
                  label="Bank Verification Status"
                  tag={
                    supplier.isBankVerified ? (
                      <Tag icon={<CheckCircleOutlined />} color="success" style={{ borderRadius: 12 }}>
                        Verified
                      </Tag>
                    ) : (
                      <Tag icon={<CloseCircleOutlined />} color="default" style={{ borderRadius: 12 }}>
                        Unverified
                      </Tag>
                    )
                  }
                />
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Section 4: Remarks / Comments */}
        {supplier.remarks && (
          <Col xs={24}>
            <Card
              title="Remarks / Notes"
              bordered={false}
              style={{ background: "#f8f9fc", borderRadius: 8 }}
            >
              <Text style={{ color: "#344054", fontSize: 14 }}>{supplier.remarks}</Text>
            </Card>
          </Col>
        )}
      </Row>

      {/* Edit Modal */}
      {isEditOpen && (
        <EditSupplierModal
          open={isEditOpen}
          supplier={supplier}
          onClose={() => setIsEditOpen(false)}
          onSuccess={() => {
            dispatch(getSupplierById(supplier.supplierId));
            onRefresh?.();
          }}
        />
      )}

      {/* Action Confirmation Modal */}
      {reasonModal.open && (
        <CustomModal
          title={reasonModal.type === "deactivate" ? `Deactivate ${supplier?.supplierName || "Supplier"}` : `Activate ${supplier?.supplierName || "Supplier"}`}
          open={reasonModal.open}
          centered
          width={500}
          onCancel={() => setReasonModal({ open: false, type: null })}
          content={
            <ReasonForm
              label={reasonModal.type === "deactivate" ? "Reason for Deactivation" : "Reason / Remarks"}
              placeholder={
                reasonModal.type === "deactivate"
                  ? "Enter the reason for deactivating this supplier..."
                  : "Enter remarks (optional)..."
              }
              btnName={reasonModal.type === "deactivate" ? "Deactivate" : "Activate"}
              btnType={reasonModal.type === "deactivate" ? "danger" : "primary"}
              submitLoading={statusLoading}
              onClose={() => setReasonModal({ open: false, type: null })}
              handleOnFormSubmit={handleStatusSubmit}
            />
          }
        />
      )}
    </div>
  );
};

export default SupplierInfo;
